import { computed, inject, Injectable, signal } from "@angular/core";
import { catchError, of, take } from "rxjs";
import { CmsService } from "libs/cms/src/lib/services/cms.lib.service";
import {
	CmsBlockPublicKey,
	RegistrationCountriesCmsBlockContent,
	RegistrationCountryOption,
} from "libs/cms/src/lib/models/cms.model";
import { RegistrationCountries } from "../registration/models/registration-countries";

export function parseRegistrationCountries(content: unknown): RegistrationCountryOption[] | null {
	if (!Array.isArray(content) || content.length === 0) {
		return null;
	}

	const parsed: RegistrationCountryOption[] = [];
	for (const item of content) {
		if (!isRegistrationCountryOption(item)) {
			return null;
		}
		const labelDe = optionalTrimmedString(item.labelDe);
		const labelEn = optionalTrimmedString(item.labelEn);
		parsed.push({
			country: item.country.trim(),
			code: item.code.trim(),
			...(labelDe ? { labelDe } : {}),
			...(labelEn ? { labelEn } : {}),
		});
	}
	return parsed;
}

function optionalTrimmedString(value: unknown): string | undefined {
	if (typeof value !== "string") {
		return undefined;
	}
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : undefined;
}

function isRegistrationCountryOption(item: unknown): item is RegistrationCountryOption {
	if (!item || typeof item !== "object") {
		return false;
	}
	const rec = item as Record<string, unknown>;
	return (
		typeof rec.country === "string" &&
		rec.country.trim().length > 0 &&
		typeof rec.code === "string" &&
		rec.code.trim().length > 0
	);
}

@Injectable({
	providedIn: "root",
})
export class RegistrationCountriesService {
	private readonly cms = inject(CmsService);
	private readonly cmsCountries = signal<RegistrationCountryOption[] | null>(null);

	readonly baseCountries = computed(() => [...(this.cmsCountries() ?? RegistrationCountries)]);

	constructor() {
		this.load();
	}

	private load(): void {
		this.cms
			.getPublicCmsBlock<RegistrationCountriesCmsBlockContent>(CmsBlockPublicKey.REGISTRATION_COUNTRIES)
			.pipe(
				take(1),
				catchError(() => of(null))
			)
			.subscribe((block) => {
				if (!block?.activated) {
					return;
				}
				const parsed = parseRegistrationCountries(block.content);
				if (parsed) {
					this.cmsCountries.set(parsed);
				}
			});
	}
}
